import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { FormGroup, Label } from "reactstrap";

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  disableSundays?: boolean;
  onlySundays?: boolean;
  disabledDates?: string[]; // "YYYY-MM-DD"
  allowedDates?: string[]; // "YYYY-MM-DD"
  multiple?: boolean;
  range?: boolean;
  minDate?: string | Date | DateObject;
  maxDate?: string | Date | DateObject;
  required?: boolean;
  labelClassName?:string;
};

const RHFDatePicker = memo(function RHFDatePicker({
  name,
  label,
  placeholder,
  className,
  disabled,
  disableSundays = false,
  onlySundays = false,
  disabledDates = [],
  allowedDates = [],
  multiple = false,
  range = false,
  minDate,
  maxDate,
  required = false,
  labelClassName
}: Props) {
  const { control } = useFormContext();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const pickerRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Close when clicking outside both the input wrapper and the (portaled) calendar
  useEffect(() => {
    if (!isOpen) return;

    const onClickAway = (e: Event) => {
      const el = e.target as Element | null;
      if (!el) return;

      const inWrapper = !!wrapperRef.current?.contains(el);
      // RMDP: .rmdp-wrapper (around input) and .rmdp-container (popup when portal)
      const inAnyRmdp = !!el.closest(".rmdp-wrapper, .rmdp-container");

      // If it's neither in our wrapper nor in ANY calendar popup, close ours.
      if (!inWrapper && !inAnyRmdp) {
        // let RMDP finish its own handlers (e.g., selection) first
        setTimeout(() => pickerRef.current?.closeCalendar?.(), 0);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") pickerRef.current?.closeCalendar?.();
    };

    // Capture phase so we see the event even if something stops propagation.
    document.addEventListener("mousedown", onClickAway, true);
    document.addEventListener("touchstart", onClickAway, true);
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", onClickAway, true);
      document.removeEventListener("touchstart", onClickAway, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen]);

  const disabledDatesSet = useMemo(
    () => new Set(disabledDates),
    [disabledDates]
  );
  const allowedDatesSet = useMemo(() => new Set(allowedDates), [allowedDates]);

  const toDateObject = (v: string) => new DateObject(v); // expects "YYYY-MM-DD"
  const normalizeValue = (val: any) => {
    if (!val) return null;
    if (Array.isArray(val)) {
      const arr = val.filter(Boolean).map(toDateObject);
      return arr.length ? arr : null;
    }
    return typeof val === "string" && val ? toDateObject(val) : null;
  };

  const mapDays = ({ date }: { date: DateObject }) => {
    const formatted = date.format("YYYY-MM-DD");

    if (allowedDatesSet.size > 0 && !allowedDatesSet.has(formatted)) {
      return {
        disabled: true,
        style: { opacity: 0.35, cursor: "not-allowed" },
      };
    }

    const isSunday = (disableSundays && date.weekDay.index === 0) ||
      (onlySundays && date.weekDay.index !== 0);
    const isDisabledDate = disabledDatesSet.has(formatted);
    if (isSunday || isDisabledDate) {
      return {
        disabled: true,
        style: {
          color: "#bbb",
          textDecoration: "line-through",
          cursor: "not-allowed",
        },
      };
    }
    return {};
  };

  const initialCurrentDate =
    allowedDates.length > 0 ? new DateObject(allowedDates[0]) : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormGroup className={className} innerRef={wrapperRef}>
          {label && <Label className={labelClassName} htmlFor={name}>{label}</Label>}
          {label && required && (
            <span
              className="ms-1 px-1 py-0 rounded-1 f-10 fw-bold"
              style={{
                background: "#FEE2E2",
                color: "#B91C1C",
                verticalAlign: "middle",
              }}
            >
              required
            </span>
          )}

          <DatePicker
            ref={pickerRef}
            value={normalizeValue(field.value)}
            onChange={(val: DateObject | DateObject[] | null) => {
              if (!val) {
                field.onChange(multiple || range ? [] : null);
              } else if (Array.isArray(val)) {
                field.onChange(val.map((d) => d.format("YYYY-MM-DD")));
              } else {
                field.onChange(val.format("YYYY-MM-DD"));
              }
              // Auto-close only for single-date selection
              if (!multiple && !range) {
                setTimeout(() => pickerRef.current?.closeCalendar?.(), 0);
              }
            }}
            format="YYYY/MM/DD"
            inputClass="form-control"
            id={name}
            placeholder={
              placeholder ??
              (multiple
                ? "Select multiple dates"
                : range
                ? "Select date range"
                : "Select date")
            }
            style={{
              width: "100%",
              ...(disabled ? { background: "#eee" } : {}),
            }}
            disabled={disabled}
            editable={allowedDates.length > 0 ? false : undefined}
            multiple={multiple}
            range={range}
            rangeHover={range}
            mapDays={mapDays}
            minDate={minDate}
            maxDate={maxDate}
            currentDate={initialCurrentDate}
            onOpenPickNewDate={false}
            portal
            zIndex={2050}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
          />

          {fieldState.error && (
            <div className="invalid-feedback d-block">
              {fieldState.error.message}
            </div>
          )}
        </FormGroup>
      )}
    />
  );
});

export default RHFDatePicker;
